import type { JSONContent } from "@tiptap/core";
import { BOX_KINDS } from "./boxes";
import type { EditorContext } from "./context";
import { type DiceTest, testNode } from "./dice_test";

// One button per box kind, drawn with the reader's own box styles.
export function kindPicker(ctx: EditorContext, pick: (cls: string) => void, current?: string): HTMLElement {
	const grid = document.createElement("div");
	grid.className = "grid grid-cols-2 gap-2";
	for (const kind of BOX_KINDS) {
		const button = document.createElement("button");
		button.type = "button";
		button.className = "odo-markdown text-left aria-pressed:ring-2 aria-pressed:ring-azul-300";
		button.setAttribute("aria-pressed", String(kind.cls === current));
		button.setAttribute("aria-label", ctx.t(kind.label));
		const sample = document.createElement("blockquote");
		sample.className = kind.cls;
		sample.textContent = ctx.t(kind.label);
		button.append(sample);
		button.addEventListener("click", () => pick(kind.cls));
		grid.append(button);
	}
	return grid;
}

export function field(label: string, input: HTMLElement): HTMLElement {
	const wrap = document.createElement("label");
	wrap.className = "flex flex-col gap-1 text-sm font-bold";
	wrap.append(label, input);
	return wrap;
}

export function textInput(value: string): HTMLInputElement {
	const input = document.createElement("input");
	input.type = "text";
	input.className = "odo-form__input-text w-full font-normal";
	input.value = value;
	return input;
}

export function selectInput(
	options: { value: string; label: string }[],
	current: string,
	className = "odo-form__select w-full font-normal",
): HTMLSelectElement {
	const select = document.createElement("select");
	select.className = className;
	for (const option of options) select.add(new Option(option.label, option.value, false, option.value === current));
	return select;
}

const row = (...cells: HTMLElement[]) => {
	const grid = document.createElement("div");
	grid.className = "grid grid-cols-2 gap-2";
	grid.append(...cells);
	return grid;
};

// The form writes a `:::teste` block; testNode refuses anything the serializer would not write back as one.
export function diceForm(ctx: EditorContext, test: DiceTest, submit: string, done: (node: JSONContent) => void): HTMLElement {
	const form = document.createElement("div");
	form.className = "flex flex-col gap-2.5";
	const [title, dice, value, pass, fail] = [test.title, test.dice, test.value, test.pass, test.fail].map(textInput);
	const op = document.createElement("select");
	op.className = "odo-form__select w-full font-normal";
	for (const symbol of ["≤", ">"]) op.add(new Option(symbol, symbol, false, symbol === test.op));
	const hint = document.createElement("p");
	hint.className = "text-sm text-ink-muted";
	hint.textContent = ctx.t("test_hint");
	const problem = document.createElement("p");
	problem.className = "hidden text-sm text-carmesim-600 dark:text-link";
	problem.setAttribute("role", "alert");
	problem.textContent = ctx.t("test_invalid");
	const button = document.createElement("button");
	button.type = "button";
	button.className = "odo-button__primary w-full py-2";
	button.textContent = submit;
	button.addEventListener("click", () => {
		const text = (input: HTMLInputElement) => input.value.trim();
		const node = testNode(ctx.manager, {
			title: text(title),
			dice: text(dice),
			more: test.more,
			op: op.value as DiceTest["op"],
			value: text(value),
			pass: text(pass),
			fail: text(fail),
		});
		problem.classList.toggle("hidden", node !== null);
		if (node) done(node);
	});
	form.append(
		field(ctx.t("test_title"), title),
		field(ctx.t("test_dice"), dice),
		row(field(ctx.t("test_op"), op), field(ctx.t("test_value"), value)),
		field(ctx.t("test_pass"), pass),
		field(ctx.t("test_fail"), fail),
		hint,
		problem,
		button,
	);
	return form;
}
